/**
 * N-291 · El listado de Presupuestos del panel: lo que se calcula sin pintar.
 *
 * - Cómo se escriben las fechas del viaje («15–19 mar 2027»).
 * - Las vistas rápidas (5A): son atajos EXACTOS de los filtros que entiende el servidor (T1):
 *   «Pedidos esta semana» = `created_from` hoy − 6 días · «Viaje en los próximos 60 días» =
 *   `travel_from` hoy y `travel_to` hoy + 60 · «Subidos a la Intranet» = `synced=1`.
 * - Filtros, orden y página en la URL (4A), de ida y de vuelta.
 */

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** Los filtros del listado que viajan por la URL, con el nombre que entiende el servidor. */
export const FILTROS = [
	'search',
	'travel_id',
	'created_from',
	'created_to',
	'travel_from',
	'travel_to',
	'synced',
	'by_customer_id',
];

/** Las columnas que el servidor sabe ordenar (un orden que no conoce, lo ignora en silencio). */
export const ORDENABLES = ['id', 'created_at', 'responsible_name_surname', 'start_date', 'price_per_student'];

export const FILAS_POR_PAGINA = [25, 50, 100];

export const VISTAS = ['all', 'week', 'next60', 'synced'];

export const TIPO_PRESUPUESTO = 1;

function opcionesPorDefecto() {
	return { page: 1, itemsPerPage: FILAS_POR_PAGINA[0], sortBy: ['created_at'], sortDesc: [true] };
}

function partes(fecha: string) {
	const [anio, mes, dia] = fecha.split('-').map(Number);
	return { anio, mes: MESES[mes - 1], dia };
}

function dosCifras(numero: number): string {
	return numero < 10 ? '0' + numero : String(numero);
}

/** Un día local como `Y-m-d`. */
export function diaISO(fecha: Date): string {
	return fecha.getFullYear() + '-' + dosCifras(fecha.getMonth() + 1) + '-' + dosCifras(fecha.getDate());
}

function sumarDias(fecha: Date, dias: number): Date {
	return new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate() + dias);
}

/** «15–19 mar 2027» · «29 mar – 2 abr 2027» · «30 dic 2026 – 3 ene 2027» · «3 may 2027». */
export function fechasDelViaje(inicio: string | null, fin: string | null): string {
	if (!inicio) {
		return '';
	}
	const i = partes(inicio);
	if (!fin || fin === inicio) {
		return `${i.dia} ${i.mes} ${i.anio}`;
	}
	const f = partes(fin);
	if (i.anio !== f.anio) {
		return `${i.dia} ${i.mes} ${i.anio} – ${f.dia} ${f.mes} ${f.anio}`;
	}
	if (i.mes !== f.mes) {
		return `${i.dia} ${i.mes} – ${f.dia} ${f.mes} ${f.anio}`;
	}
	return `${i.dia}–${f.dia} ${f.mes} ${f.anio}`;
}

/** 5706 → «5.706». */
export function miles(numero: number | null | undefined): string {
	if (numero === null || numero === undefined) {
		return '';
	}
	return String(numero).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/** Los filtros que están puestos (sin el tipo ni los vacíos), como texto. */
export function filtrosPuestos(filtros: { [clave: string]: any }): { [clave: string]: string } {
	const puestos = {};
	for (const clave of FILTROS) {
		const valor = filtros[clave];
		if (valor !== undefined && valor !== null && valor !== '') {
			puestos[clave] = String(valor);
		}
	}
	return puestos;
}

/** Los filtros que pone cada vista rápida. */
export function filtrosDeVista(vista: string, hoy: Date): { [clave: string]: string } {
	if (vista === 'week') {
		return { created_from: diaISO(sumarDias(hoy, -6)) };
	}
	if (vista === 'next60') {
		return { travel_from: diaISO(hoy), travel_to: diaISO(sumarDias(hoy, 60)) };
	}
	if (vista === 'synced') {
		return { synced: '1' };
	}
	return {};
}

function mismosFiltros(a: { [clave: string]: string }, b: { [clave: string]: string }): boolean {
	const claves = Object.keys(a);
	return claves.length === Object.keys(b).length && claves.every(clave => a[clave] === b[clave]);
}

/** La vista cuyos filtros son EXACTAMENTE los puestos, o null. */
export function vistaActiva(filtros: { [clave: string]: any }, hoy: Date): string | null {
	const puestos = filtrosPuestos(filtros);
	const vista = VISTAS.find(v => mismosFiltros(puestos, filtrosDeVista(v, hoy)));
	return vista === undefined ? null : vista;
}

/** Lo que va en la URL: los filtros puestos y lo que no sea lo de por defecto. */
export function consultaDe(
	filtros: { [clave: string]: any },
	opciones: { page?: number; itemsPerPage?: number; sortBy?: string[]; sortDesc?: boolean[] },
	profesor: string | null = null
): { [clave: string]: string } {
	const consulta = filtrosPuestos(filtros);
	if (consulta.by_customer_id && profesor) {
		consulta.profesor = profesor;
	}
	const porDefecto = opcionesPorDefecto();
	if (opciones.page && opciones.page !== porDefecto.page) {
		consulta.page = String(opciones.page);
	}
	if (opciones.itemsPerPage && opciones.itemsPerPage !== porDefecto.itemsPerPage) {
		consulta.per_page = String(opciones.itemsPerPage);
	}
	if (opciones.sortBy && opciones.sortBy.length) {
		const orden = (opciones.sortDesc && opciones.sortDesc[0] ? '-' : '') + opciones.sortBy[0];
		if (orden !== '-created_at') {
			consulta.sort = orden;
		}
	}
	return consulta;
}

function valorDeConsulta(valor: any): string | null {
	if (Array.isArray(valor)) {
		valor = valor[0];
	}
	return typeof valor === 'string' && valor !== '' ? valor : null;
}

/** De la URL a la tabla. Lo que no vale (filas que no se ofrecen, orden desconocido…) se ignora. */
export function estadoDe(consulta: { [clave: string]: any }) {
	const filtros: { [clave: string]: any } = { type: TIPO_PRESUPUESTO };
	for (const clave of FILTROS) {
		const valor = valorDeConsulta(consulta[clave]);
		if (valor !== null && (clave !== 'synced' || valor === '0' || valor === '1')) {
			filtros[clave] = valor;
		}
	}
	const opciones = opcionesPorDefecto();
	const pagina = Number(valorDeConsulta(consulta.page));
	if (Number.isInteger(pagina) && pagina > 1) {
		opciones.page = pagina;
	}
	const filas = Number(valorDeConsulta(consulta.per_page));
	if (FILAS_POR_PAGINA.indexOf(filas) !== -1) {
		opciones.itemsPerPage = filas;
	}
	const orden = valorDeConsulta(consulta.sort);
	if (orden !== null) {
		const columna = orden.replace(/^-/, '');
		if (ORDENABLES.indexOf(columna) !== -1) {
			opciones.sortBy = [columna];
			opciones.sortDesc = [orden.charAt(0) === '-'];
		}
	}
	const profesor = filtros.by_customer_id ? valorDeConsulta(consulta.profesor) : null;
	return { filtros, opciones, profesor };
}
